import { Device, Call } from "@twilio/voice-sdk"
import { api } from "../api/client"
import { useCallStore, IncomingCall, CurrentCall } from "../../stores/call-store"
import { getSocket } from "../socket"
import { hydrateCurrentCallFromServer } from "../call-center/current-call-sync"
import { showIncomingCallAlert, dismissIncomingCallAlert, claimAudioLock } from "./incoming-call-alerts"

// Socket event names
const JOIN_AGENT_ROOM = "JOIN_AGENT_ROOM"

// Safari 18 added setSinkId() support but requires a user gesture. The Twilio SDK
// calls setSinkId('default') during Device construction and audio helper init, but
// by that point the gesture context has expired due to the async token fetch. This
// causes an unhandled promise rejection. Since this app doesn't use audio output
// device selection, we patch setSinkId to gracefully swallow the error.
if (typeof HTMLAudioElement !== "undefined" && (HTMLAudioElement.prototype as any).setSinkId) {
  const origSetSinkId = (HTMLAudioElement.prototype as any).setSinkId
  ;(HTMLAudioElement.prototype as any).setSinkId = function (sinkId: string) {
    return origSetSinkId.call(this, sinkId).catch(() => {
      // Safari requires user gesture for setSinkId — safe to ignore
    })
  }
}

// Store device instance globally
let twilioDevice: Device | null = null
let activeCall: Call | null = null
let pendingLongestIdleInfo: {
  name: string
  number: string
  contact: string
  address: string
  call_queue: string
  queue_entry_title: string
  queue_entry_color: string
  division_name: string
} | null = null
let pendingIncomingCall: Call | null = null
let incomingCancelTimer: ReturnType<typeof setTimeout> | null = null
let staleCallCheckInterval: ReturnType<typeof setInterval> | null = null
let activeCallReconcileInterval: ReturnType<typeof setInterval> | null = null

// Recovery state — auto-recovers device after transient errors without
// removing the user from the call queue
let recoveryTimer: ReturnType<typeof setTimeout> | null = null
let recoveryAttempts = 0
const MAX_RECOVERY_ATTEMPTS = 3
const RECOVERY_BASE_DELAY_MS = 2000
const ACTIVE_CALL_RECONCILE_INTERVAL_MS = 5000
const LIVE_CALL_STATUSES = ["open", "connecting", "pending", "ringing", "reconnecting"]
let socketListenersAttached = false

function hasLiveActiveCall(call: Call | null = activeCall): boolean {
  if (!call) return false
  return LIVE_CALL_STATUSES.includes(call.status())
}

function clearCallRuntimeState(reason: string): void {
  stopStaleCallCheck()
  stopActiveCallReconciler()
  if (incomingCancelTimer) {
    clearTimeout(incomingCancelTimer)
    incomingCancelTimer = null
  }
  activeCall = null
  pendingIncomingCall = null
  pendingLongestIdleInfo = null
  useCallStore.getState().resetCallState()
  dismissIncomingCallAlert()
  console.warn(`[twilio] Cleared call runtime state (${reason})`)
}

function startActiveCallReconciler(): void {
  if (activeCallReconcileInterval) return

  activeCallReconcileInterval = setInterval(() => {
    void reconcileActiveCallState("interval")
  }, ACTIVE_CALL_RECONCILE_INTERVAL_MS)
}

function stopActiveCallReconciler(): void {
  if (activeCallReconcileInterval) {
    clearInterval(activeCallReconcileInterval)
    activeCallReconcileInterval = null
  }
}

// Polls the Twilio Call object's status to detect stale incoming calls that
// weren't cleaned up by the normal cancel/reject/error events (e.g. browser
// tab was backgrounded, network blip, SDK quirk).
function startStaleCallCheck(call: Call): void {
  stopStaleCallCheck()
  staleCallCheckInterval = setInterval(() => {
    if (!pendingIncomingCall || pendingIncomingCall !== call) {
      stopStaleCallCheck()
      return
    }
    const status = call.status()
    if (status === "closed") {
      clearCallRuntimeState("incoming-stale-check")
    }
  }, 3000)
}

function stopStaleCallCheck(): void {
  if (staleCallCheckInterval) {
    clearInterval(staleCallCheckInterval)
    staleCallCheckInterval = null
  }
}

export async function reconcileActiveCallState(reason = "manual"): Promise<CurrentCall | null> {
  const currentCall = useCallStore.getState().currentCall
  if (!currentCall) return null

  if (activeCall) {
    const status = activeCall.status()

    if (hasLiveActiveCall(activeCall)) {
      return currentCall
    }

    if (status === "closed") {
      console.warn(`[twilio] Clearing stale currentCall because activeCall is closed (${reason})`)
      clearCallRuntimeState(`active-call-closed:${reason}`)
      return null
    }
  }

  return hydrateCurrentCallFromServer(`twilio:${reason}`, {
    clearIfMissing: true,
    hasLiveTwilioCall: hasLiveActiveCall(activeCall),
  })
}

// Play ringtone sound (only if this tab holds the cross-tab audio lock)
export const playSound = (tries = 0): void => {
  if (!twilioDevice) return
  if (!claimAudioLock()) return

  try {
    const audio = new Audio("/Ringtone.m4a")
    audio.play().catch((e) => {
      if (tries > 10) return
      setTimeout(() => {
        playSound(tries + 1)
      }, 100)
    })
  } catch (e) {
    console.error("Error playing sound:", e)
  }
}

// Send DTMF digits during a call
export const sendDigits = (digit: string): boolean => {
  if (!activeCall) {
    console.error("No active call to send digits to")
    return false
  }

  const callStatus = activeCall.status()
  if (callStatus !== "open" && callStatus !== "pending" && callStatus !== "connecting") {
    console.error("Call is not in a valid state to send digits:", callStatus)
    activeCall = null
    return false
  }

  try {
    activeCall.sendDigits(digit)
    return true
  } catch (error) {
    console.error("Error sending digits:", error)
    return false
  }
}

// Toggle mute on active call
export const toggleMute = (): boolean => {
  // Try to find call from device if activeCall is not set
  if (!activeCall && twilioDevice) {
    const calls = twilioDevice.calls
    if (calls) {
      const callsArray = Array.from(calls || [])
      activeCall =
        callsArray.find((call) => {
          const status = call.status()
          return status === "open" || status === "pending" || status === "connecting"
        }) || null
    }
  }

  if (!activeCall) {
    return false
  }

  const callStatus = activeCall.status()
  if (callStatus !== "open" && callStatus !== "pending" && callStatus !== "connecting") {
    activeCall = null
    return false
  }

  try {
    const isMuted = activeCall.isMuted()
    activeCall.mute(!isMuted)
    useCallStore.getState().setMuted(!isMuted)
    return !isMuted
  } catch (error) {
    console.error("Error toggling mute:", error)
    return false
  }
}

// Extract call parameters from Twilio call object
const getCallParams = (call: Call): IncomingCall | CurrentCall => {
  const customParams = call.customParameters || new Map()
  const params = call.parameters || {}

  return {
    matter: customParams.get("matter") || "",
    contact: customParams.get("contact") || "",
    name: customParams.get("name") || "",
    number: customParams.get("number") || "",
    address: customParams.get("address") || "",
    call_sid: params.CallSid || "",
  }
}

// Fetch token and create Twilio device
const fetchAndSetToken = async (): Promise<boolean> => {
  try {
    const token = await api.callCenter.accessToken()

    if (!token.data?.token) {
      console.error("Invalid Twilio Configuration")
      useCallStore.getState().setBrowserDialing("error")
      return false
    }

    twilioDevice = new Device(token.data.token, {
      codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
      closeProtection: true,
      edge: "ashburn",
      allowIncomingWhileBusy: true,
    })

    return true
  } catch (e) {
    console.error("Error setting up Twilio device:", e)
    useCallStore.getState().setBrowserDialing("error")
    return false
  }
}

// Attach socket event listeners for call center features (transfer, hold, longest idle).
// Only attaches once — safe to call multiple times.
function attachSocketListeners(): void {
  if (socketListenersAttached) return
  const socket = getSocket()
  if (!socket) return

  socket.on("CALL_CENTER.TRANSFER_DID_NOT_ANSWER", () => {
    useCallStore.getState().setTransferCall(null)
  })

  socket.on("CALL_CENTER.TRANSFER_ANSWERED", () => {
    const current = useCallStore.getState().transferCall
    if (current) {
      useCallStore.getState().setTransferCall({ ...current, can_merge: true })
    }
  })

  socket.on("CALL_CENTER.HOLD_TOGGLED", (params: { hold?: boolean }) => {
    if (params.hold !== undefined) {
      useCallStore.getState().setOnHold(params.hold)
    }
  })

  socket.on("CALL_CENTER.LONGEST_IDLE_RINGING", (data: {
    name?: string
    number?: string
    contact?: string
    call_queue?: string
    queue_entry_title?: string
    queue_entry_color?: string
    division_name?: string
    city?: string
    state?: string
  }) => {
    const address = [data.city, data.state].filter(Boolean).join(", ")
    const info = {
      name: data.name || "",
      number: data.number || "",
      contact: data.contact || "",
      address,
      call_queue: data.call_queue || "",
      queue_entry_title: data.queue_entry_title || "",
      queue_entry_color: data.queue_entry_color || "",
      division_name: data.division_name || "",
    }

    const store = useCallStore.getState()
    if (store.incomingCall && !store.incomingCall.name && !store.incomingCall.number) {
      store.setIncomingCall({
        ...store.incomingCall,
        ...info,
      })
      pendingLongestIdleInfo = null
    } else {
      pendingLongestIdleInfo = info
    }
  })

  socketListenersAttached = true
}

// Attach all Twilio Device event listeners. Extracted so both enableDialing()
// and recoverDevice() can use it when creating a new device instance.
function attachDeviceListeners(device: Device): void {
  // Device registered successfully
  device.on("registered", () => {
    const socket = getSocket()
    if (socket) {
      socket.emit(JOIN_AGENT_ROOM, {})
      attachSocketListeners()
    }
    useCallStore.getState().setBrowserDialing("ready")
  })

  // Handle errors — do NOT leave agent room for transient errors.
  // User intent (isInQueue) should only change via explicit user action.
  device.on("error", async (error) => {
    console.error("Twilio Device Error:", error)

    // Token expired — refresh the token, device handles re-registration
    if (error.code === 31205) {
      try {
        const token = await api.callCenter.accessToken()
        if (token.data?.token && twilioDevice) {
          await twilioDevice.updateToken(token.data.token)
        }
      } catch (e) {
        console.error("Error refreshing token:", e)
        scheduleRecovery()
      }
      return
    }

    // For other errors: check if device is still functional
    if (twilioDevice && twilioDevice.state === "registered") {
      return // Device recovered on its own
    }

    // Device is dead — attempt recovery without removing user from queue
    scheduleRecovery()
  })

  // Token will expire soon
  device.on("tokenWillExpire", async () => {
    try {
      const token = await api.callCenter.accessToken()
      if (token.data?.token && twilioDevice) {
        await twilioDevice.updateToken(token.data.token)
      }
    } catch (e) {
      console.error("Error refreshing token:", e)
    }
  })

  // Handle incoming calls
  device.on("incoming", (call: Call) => {
    // Clean up stale activeCall from a previous call that wasn't properly torn down
    if (activeCall) {
      const status = activeCall.status()
      if (status === "closed") {
        clearCallRuntimeState("incoming-with-closed-active-call")
      }
    }

    // Only reject if we have a genuinely live call (not stale state)
    if (hasLiveActiveCall(activeCall)) {
      try {
        call.reject()
      } catch (e) {
        console.error("Error rejecting incoming call:", e)
      }
      return
    }

    // Clear any stale store state before accepting the new call
    const currentState = useCallStore.getState()
    if (!activeCall && currentState.currentCall) {
      clearCallRuntimeState("stale-current-call-before-incoming")
    }

    // Clear any pending cancel/error timer from a previous call so the modal
    // stays visible when a call is rapidly re-offered after cancellation.
    if (incomingCancelTimer) {
      clearTimeout(incomingCancelTimer)
      incomingCancelTimer = null
    }

    let callParams = getCallParams(call)

    // REST API calls (longest idle) don't have custom parameters — use socket data
    if (!callParams.name && !callParams.number && pendingLongestIdleInfo) {
      callParams = { ...callParams, ...pendingLongestIdleInfo }
      pendingLongestIdleInfo = null
    }

    pendingIncomingCall = call
    useCallStore.getState().setIncomingCall(callParams as IncomingCall)
    showIncomingCallAlert(callParams.name || "", callParams.number || "")
    startStaleCallCheck(call)

    // Call accepted (no guard — acceptCall() clears pendingIncomingCall before
    // the SDK fires this event, so the guard would incorrectly block the
    // state transition to currentCall)
    call.on("accept", () => {
      console.log("[twilio] incoming call accepted")
      stopStaleCallCheck()
      activeCall = call
      pendingIncomingCall = null
      useCallStore.getState().setIncomingCall(null)
      useCallStore.getState().setCurrentCall(callParams as CurrentCall)
      dismissIncomingCallAlert()
    })

    // Call disconnected (no guard — fires on active calls after accept)
    call.on("disconnect", () => {
      console.log("[twilio] incoming call disconnected")
      clearCallRuntimeState("incoming-disconnect")
    })

    // Call cancelled — guarded so a stale call's cancel doesn't wipe a newer
    // incoming call's state. Debounced 500ms to bridge rapid re-offers.
    call.on("cancel", () => {
      console.log("[twilio] incoming call cancelled — pendingMatch:", pendingIncomingCall === call)
      stopStaleCallCheck()
      if (pendingIncomingCall !== call) return
      incomingCancelTimer = setTimeout(() => {
        if (pendingIncomingCall === call) {
          clearCallRuntimeState("incoming-cancel")
        }
      }, 500)
    })

    // Call rejected — guarded (rejectCall() already handles its own cleanup)
    call.on("reject", () => {
      console.log("[twilio] incoming call rejected — pendingMatch:", pendingIncomingCall === call)
      stopStaleCallCheck()
      if (pendingIncomingCall !== call) return
      clearCallRuntimeState("incoming-reject")
    })

    // Call error — guarded + debounced like cancel
    call.on("error", (error) => {
      console.error("[twilio] incoming call error:", error, "— pendingMatch:", pendingIncomingCall === call)
      stopStaleCallCheck()
      if (pendingIncomingCall !== call) return
      incomingCancelTimer = setTimeout(() => {
        if (pendingIncomingCall === call) {
          clearCallRuntimeState("incoming-error")
        }
      }, 500)
    })

    // Call quality warnings for diagnostics
    call.on("warning", (warningName: string) => {
      console.warn("Call warning:", warningName)
    })

    call.on("warning-cleared", (warningName: string) => {
    })
  })

  // Device unregistered — auto-recover if user intended to be in queue
  device.on("unregistered", () => {
    console.warn("[twilio] Device unregistered")
    if (useCallStore.getState().isInQueue) {
      scheduleRecovery()
    }
  })
}

// Cancel any pending recovery timer and reset attempt counter
function cancelRecovery(): void {
  if (recoveryTimer) {
    clearTimeout(recoveryTimer)
    recoveryTimer = null
  }
  recoveryAttempts = 0
}

// Schedule a device recovery attempt with exponential backoff (2s, 4s, 8s)
function scheduleRecovery(): void {
  if (recoveryTimer) return

  if (hasLiveActiveCall(activeCall)) {
    return
  }

  if (recoveryAttempts >= MAX_RECOVERY_ATTEMPTS) {
    console.error("[twilio] Max recovery attempts reached")
    useCallStore.getState().setBrowserDialing("error")
    return
  }

  const delay = RECOVERY_BASE_DELAY_MS * Math.pow(2, recoveryAttempts)
  console.warn(`[twilio] Scheduling recovery attempt ${recoveryAttempts + 1}/${MAX_RECOVERY_ATTEMPTS} in ${delay}ms`)

  recoveryTimer = setTimeout(async () => {
    recoveryTimer = null
    recoveryAttempts++
    await recoverDevice()
  }, delay)
}

// Destroy dead device and create a new one
async function recoverDevice(): Promise<void> {
  clearCallRuntimeState("recover-device")

  if (twilioDevice) {
    try { twilioDevice.destroy() } catch { /* ignore errors on dead device */ }
    twilioDevice = null
  }

  const set = await fetchAndSetToken()
  // fetchAndSetToken() reassigns the module-level twilioDevice — TS can't track that
  const device = twilioDevice as Device | null
  if (!set || !device) {
    scheduleRecovery()
    return
  }

  attachDeviceListeners(device)

  try {
    await device.register()
    cancelRecovery()
    useCallStore.getState().setBrowserDialing("ready")
    console.log("[twilio] Device recovered successfully")
  } catch (error) {
    console.error("[twilio] Recovery registration failed:", error)
    scheduleRecovery()
  }
}

// Enable browser dialing - main setup function
export const enableDialing = async (): Promise<boolean> => {
  const store = useCallStore.getState()

  if (store.browserDialing === "loading") return false

  // If we think we're ready, verify the device is actually alive
  if (store.browserDialing === "ready") {
    if (twilioDevice && twilioDevice.state === "registered") {
      // Device is genuinely ready — re-join agent room in case we were knocked out
      const socket = getSocket()
      if (socket) {
        socket.emit(JOIN_AGENT_ROOM, {})
      }
      return true
    }
    // Device is dead despite "ready" state — fall through to re-initialize
    console.warn("[twilio] enableDialing: browserDialing is 'ready' but device is dead, re-initializing")
  }

  cancelRecovery()
  store.setBrowserDialing("loading")

  const set = await fetchAndSetToken()
  if (!set || !twilioDevice) return false

  attachDeviceListeners(twilioDevice)

  // Register the device
  try {
    await twilioDevice.register()
    return true
  } catch (error) {
    console.error("Failed to register device:", error)
    useCallStore.getState().setBrowserDialing("error")
    return false
  }
}

// Accept incoming call
export const acceptCall = (): void => {
  if (incomingCancelTimer) {
    clearTimeout(incomingCancelTimer)
    incomingCancelTimer = null
  }
  dismissIncomingCallAlert()

  // Use the captured call object from the incoming event
  if (pendingIncomingCall) {
    const status = pendingIncomingCall.status()
    if (status === "pending") {
      try {
        pendingIncomingCall.accept()
        activeCall = pendingIncomingCall
        pendingIncomingCall = null
        return
      } catch (error) {
        console.error("Error accepting call:", error)
        pendingIncomingCall = null
        activeCall = null
        useCallStore.getState().setIncomingCall(null)
        // Fall through to fallback search
      }
    } else {
      // Call timed out or was cancelled — clear stale reference
      pendingIncomingCall = null
      // Fall through to fallback search
    }
  }

  // Fallback: search device.calls for any pending call
  if (!twilioDevice) {
    useCallStore.getState().setIncomingCall(null)
    return
  }
  const calls = twilioDevice.calls
  if (calls) {
    const callsArray = Array.from(calls)
    const incomingCall = callsArray.find((c) => c.status() === "pending")
    if (incomingCall) {
      try {
        incomingCall.accept()
        activeCall = incomingCall
      } catch (error) {
        console.error("Error accepting fallback call:", error)
        useCallStore.getState().setIncomingCall(null)
      }
    } else {
      // No pending calls found — clear the ringing UI
      useCallStore.getState().setIncomingCall(null)
    }
  } else {
    useCallStore.getState().setIncomingCall(null)
  }
}

// Reject incoming call
export const rejectCall = (): void => {
  if (incomingCancelTimer) {
    clearTimeout(incomingCancelTimer)
    incomingCancelTimer = null
  }
  dismissIncomingCallAlert()

  // Use the captured call object from the incoming event
  if (pendingIncomingCall) {
    pendingIncomingCall.reject()
    pendingIncomingCall = null
    useCallStore.getState().setIncomingCall(null)
    return
  }

  // Fallback: search device.calls
  if (!twilioDevice) return
  const calls = twilioDevice.calls
  if (calls) {
    const callsArray = Array.from(calls)
    const incomingCall = callsArray.find((c) => c.status() === "pending")
    if (incomingCall) {
      incomingCall.reject()
    }
  }
  useCallStore.getState().setIncomingCall(null)
}

// Dial a number
export const dialNumber = async (params: {
  name?: string
  address?: string
  number: string
  contact?: string
  division?: string
  matter?: string
  call_queue_entry?: string
}): Promise<Call | null> => {
  if (!twilioDevice) {
    console.error("Phone system not initialized")
    return null
  }

  if (!params.division) {
    console.warn("[twilio] dialNumber called without division — call will likely fail with server error 3")
  }

  try {
    const callParams = {
      To: params.number || "",
      name: params.name || "",
      address: params.address || "",
      number: params.number || "",
      contact: params.contact || "",
      division: params.division || "",
      matter: params.matter || "",
      call_queue_entry: params.call_queue_entry || "",
    }


    const call = await twilioDevice.connect({ params: callParams })
    activeCall = call

    // Set up call event handlers
    call.on("accept", () => {
      activeCall = call
      // Use the known params we already have — outbound calls don't populate
      // call.parameters.CallSid, so getCallParams() would return an empty call_sid.
      const callSid = (call.parameters as Record<string, string>)?.CallSid || ""
      useCallStore.getState().setCurrentCall({
        name: params.name || "",
        number: params.number || "",
        address: params.address || "",
        contact: params.contact || "",
        matter: params.matter || "",
        call_sid: callSid,
      })
    })

    call.on("disconnect", () => {
      clearCallRuntimeState("outgoing-disconnect")
    })

    call.on("cancel", () => {
      clearCallRuntimeState("outgoing-cancel")
    })

    // Error handler prevents stale state from blocking future incoming calls
    call.on("error", (error) => {
      console.error("Outgoing call error:", error)
      clearCallRuntimeState("outgoing-error")
    })

    call.on("warning", (warningName: string) => {
      console.warn("Outgoing call warning:", warningName)
    })

    return call
  } catch (error) {
    console.error("Failed to dial:", error)
    return null
  }
}

// End all calls
export const disconnectAll = (): void => {
  if (activeCall) {
    try {
      activeCall.disconnect()
    } catch (error) {
      console.error("Error disconnecting call:", error)
    }
  }

  clearCallRuntimeState("disconnect-all")
}

// Get the active call
export const getActiveCall = (): Call | null => {
  return activeCall
}

// Cleanup Twilio device
export const cleanup = (): void => {
  cancelRecovery()
  clearCallRuntimeState("cleanup")
  if (twilioDevice) {
    twilioDevice.destroy()
    twilioDevice = null
  }
  useCallStore.getState().setIsInQueue(false)
}

// Check if device is ready
export const isDeviceReady = (): boolean => {
  return twilioDevice !== null && useCallStore.getState().browserDialing === "ready"
}

let trackedCurrentCallKey =
  useCallStore.getState().currentCall?.call_id ||
  useCallStore.getState().currentCall?.call_sid ||
  null

useCallStore.subscribe((state) => {
  const nextCurrentCallKey = state.currentCall?.call_id || state.currentCall?.call_sid || null
  if (nextCurrentCallKey === trackedCurrentCallKey) {
    return
  }

  trackedCurrentCallKey = nextCurrentCallKey

  if (nextCurrentCallKey) {
    startActiveCallReconciler()
    void reconcileActiveCallState("store-sync")
    return
  }

  stopActiveCallReconciler()
})

// Clean up Twilio device on tab close for a clean WebSocket shutdown.
// Don't emit LEAVE_AGENT_ROOM — the server's socket disconnect handler
// already removes the user from the room when the socket closes.
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", () => {
    if (twilioDevice) {
      twilioDevice.destroy()
      twilioDevice = null
    }
  })
}
